import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Phone, Tag, Calendar } from "lucide-react";
import Header from "@/components/Header";
import TopAlertBar from "@/components/TopAlertBar";
import Footer from "@/components/Footer";
import FloatingCallButton from "@/components/FloatingCallButton";
import WhatsAppButton from "@/components/WhatsAppButton";
import { ZoneProvider } from "@/lib/ZoneContext";
import { DEFAULT_ZONE } from "@/lib/zones";
import { BLOG_POSTS, getPostBySlug } from "@/lib/blogPosts";
import { SITE } from "@/lib/siteConfig";
import useScrollReveal from "@/lib/useScrollReveal";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  useScrollReveal();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} · SOS Serrurier Express`;
    }
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <ZoneProvider zone={DEFAULT_ZONE}>
      <div className="min-h-screen bg-white">
        <TopAlertBar />
        <Header />

        <main>
          {/* Hero article */}
          <section className="relative bg-[#0A0A0A] text-white pt-16 sm:pt-20 pb-20 sm:pb-28 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={post.cover}
                alt=""
                className="w-full h-full object-cover opacity-30"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/60" />
            </div>
            <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link
                to="/blog"
                data-testid="blog-back"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-slate-300 hover:text-red-400 mb-6"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Retour au blog
              </Link>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs uppercase tracking-[0.2em] font-bold text-red-400 mb-6">
                <span className="inline-flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" />
                  {post.category}
                </span>
                <span className="text-slate-500">·</span>
                <span className="inline-flex items-center gap-1.5 text-slate-300">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(post.date)}
                </span>
                <span className="text-slate-500">·</span>
                <span className="inline-flex items-center gap-1.5 text-slate-300">
                  <Clock className="w-3.5 h-3.5" />
                  {post.readMin} min de lecture
                </span>
              </div>
              <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
                {post.title}
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-3xl">{post.excerpt}</p>
            </div>
          </section>

          {/* Content */}
          <article className="bg-white py-14 sm:py-20 reveal">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              {post.content.map((block, i) => {
                if (block.type === "h2") {
                  return (
                    <h2
                      key={i}
                      className="font-display font-black tracking-tight text-2xl sm:text-3xl uppercase text-[#0A0A0A] mt-12 mb-5 leading-tight"
                    >
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === "p") {
                  return (
                    <p
                      key={i}
                      className="text-base sm:text-lg text-slate-700 leading-relaxed mb-5 whitespace-pre-line"
                    >
                      {block.text}
                    </p>
                  );
                }
                if (block.type === "cta") {
                  return (
                    <div
                      key={i}
                      data-testid="blog-cta"
                      className="my-10 bg-[#0A0A0A] text-white p-7 sm:p-9 border-l-4 border-red-600 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5"
                    >
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-red-400 mb-2">
                          — Besoin d'aide maintenant ?
                        </div>
                        <div className="font-display font-black text-xl sm:text-2xl uppercase leading-tight">
                          Nos serruriers interviennent en moins de 30&nbsp;min
                        </div>
                        <div className="text-sm text-slate-300 mt-1">
                          Devis annoncé par téléphone · 24h/24 · 7j/7
                        </div>
                      </div>
                      <a
                        href={SITE.phoneHref}
                        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-3 font-display font-black uppercase tracking-tight transition-all shadow-[0_4px_0_#991B1B] hover:shadow-[0_2px_0_#991B1B] hover:-translate-y-0.5 whitespace-nowrap"
                      >
                        <Phone className="w-4 h-4" strokeWidth={2.5} />
                        {SITE.phone}
                      </a>
                    </div>
                  );
                }
                return null;
              })}

              {/* Tags */}
              {post.keywords?.length > 0 && (
                <div className="mt-12 pt-8 border-t border-black/10 flex flex-wrap gap-2">
                  {post.keywords.map((k) => (
                    <span
                      key={k}
                      className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600 border border-black/10 px-3 py-1.5"
                    >
                      #{k}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </article>

          {/* Related */}
          <section className="bg-[#F3F4F6] py-16 sm:py-20 reveal">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-xs tracking-[0.25em] uppercase font-bold text-red-600 mb-4">
                — À lire aussi
              </div>
              <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl uppercase leading-[0.95] text-[#0A0A0A] mb-10">
                Continuez la lecture
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-related-${p.slug}`}
                    className="group bg-white border border-black/10 overflow-hidden hover:-translate-y-1 transition-all hover:shadow-xl"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={p.cover}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-6">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-red-600 mb-3">
                        {p.category}
                      </div>
                      <h3 className="font-display font-black tracking-tight text-lg sm:text-xl uppercase leading-tight text-[#0A0A0A] group-hover:text-red-600 transition">
                        {p.title}
                      </h3>
                      <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-red-600 group-hover:gap-2.5 transition-all">
                        Lire
                        <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingCallButton />
        <WhatsAppButton />
      </div>
    </ZoneProvider>
  );
}
